import { createCampaign as createCampaignAction } from 'actions/campaigns'
import { alert } from 'actions/flash'
import { getTargetSegment } from 'actions/targetSegments'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import type { Fields as TargetSegment } from 'models/TargetSegment'
import { useCallback, useState } from 'react'
import { push } from 'react-router-redux'

const t = buildTranslate('segments.landing')

/**
 * Custom hook for managing segment campaign creation workflow.
 * Handles segment fetching, modal state, and campaign creation/navigation.
 *
 * The `openCampaignModal` function accepts either:
 * - A segment ID (number) - will fetch the full segment data
 * - A full segment object - will use it directly (no API call)
 *
 * Used in AISegmentBuilderPage, AISegmentBuilder modal, and Segments Landing page.
 */
export function useSegmentCampaign() {
  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState<TargetSegment | null>(
    null
  )
  const [isLoadingSegment, setIsLoadingSegment] = useState(false)

  /**
   * Opens the campaign creation modal for a segment.
   * Accepts either a segment ID (fetches full data) or a full segment object.
   */
  const openCampaignModal = useCallback(
    async (segmentOrId: TargetSegment | number) => {
      // If passed a full segment object, use it directly
      if (typeof segmentOrId === 'object' && segmentOrId !== null) {
        setSelectedSegment(segmentOrId)
        setShowModal(true)
        return
      }

      // Otherwise, fetch the segment by ID
      const segmentId = segmentOrId
      setIsLoadingSegment(true)

      try {
        // Fetch the full segment data
        const result = await dispatch(getTargetSegment(segmentId))
        if (result.error) {
          dispatch(
            alert({
              key: 'danger',
              message: t('new_segment_campaign_modal.errors.submit'),
            })
          )
          setIsLoadingSegment(false)
          return
        }

        const segment = result.payload.data.target_segment
        setSelectedSegment(segment)
        setShowModal(true)
        setIsLoadingSegment(false)
      } catch (error) {
        dispatch(
          alert({
            key: 'danger',
            message: t('new_segment_campaign_modal.errors.submit'),
          })
        )
        setIsLoadingSegment(false)
      }
    },
    [dispatch]
  )

  /**
   * Closes the modal and clears selected segment
   */
  const closeModal = useCallback(() => {
    setSelectedSegment(null)
    setShowModal(false)
  }, [])

  /**
   * Creates a campaign for the given segment and navigates to it
   */
  const createCampaign = useCallback(
    async (segment: TargetSegment, type: 'targeted' | 'automated') => {
      const params = {
        campaign: {
          type: type === 'targeted' ? 'custom' : 'custom_automated',
          target_segment_id: segment.id,
        },
      }

      const action = await dispatch(createCampaignAction(params))
      if (action.error) {
        dispatch(
          alert({
            key: 'danger',
            message: t('new_segment_campaign_modal.errors.submit'),
          })
        )
        return
      }

      const campaignId = action.payload?.data?.campaign?.id
      if (campaignId) {
        const path =
          type === 'targeted'
            ? `/thanx_campaigns/targeted/custom/${campaignId}`
            : `/thanx_campaigns/automated/custom_automated/${campaignId}`
        dispatch(push(path))
      } else {
        dispatch(
          alert({
            key: 'danger',
            message: t('new_segment_campaign_modal.errors.submit'),
          })
        )
      }
    },
    [dispatch]
  )

  return {
    // State
    showModal,
    selectedSegment,
    isLoadingSegment,
    // Actions
    openCampaignModal,
    closeModal,
    createCampaign,
  }
}

export default useSegmentCampaign
